// ── Fetch helpers ─────────────────────────────────────────────────────────────

import type { PostFeedingReportMessageDto } from "@strtmn/sds/types"
import type { FetchResults, FetchStatus, FeedingSummaryRow } from "./types"

const CONCURRENCY = 8
const MAX_RETRIES = 3
const BASE_DELAY_MS = 300

const RETRYABLE = new Set([408, 429, 500, 502, 503, 504])

async function fetchWithRetry(
  id: string,
  signal: AbortSignal,
  _fetchDetail: (id: string) => Promise<PostFeedingReportMessageDto>
): Promise<FetchStatus> {
  let lastErr = ""
  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      const response = await _fetchDetail(id)
      return { status: "ok", response }
    } catch (err: any) {
      if (signal.aborted) {
        return { status: "failed", error: "Aborted" }
      }
      const status: number | undefined = err?.status
      if (status && !RETRYABLE.has(status)) {
        return { status: "failed", error: `HTTP ${status}` }
      }
      lastErr = err?.message ?? String(err)
      if (attempt < MAX_RETRIES - 1) {
        const delay =
          BASE_DELAY_MS * 2 ** attempt * (0.75 + Math.random() * 0.5)
        await new Promise((r) => setTimeout(r, delay))
      }
    }
  }
  return { status: "failed", error: lastErr || "Unknown error" }
}

export async function fetchAllDetails(
  rows: FeedingSummaryRow[],
  fetchDetail: (id: string) => Promise<PostFeedingReportMessageDto>,
  signal: AbortSignal,
  onProgress: (done: number) => void
): Promise<FetchResults> {
  const results: FetchResults = new Map()
  let done = 0
  const queue = [...rows]
  async function worker() {
    while (queue.length > 0) {
      const row = queue.shift()!
      if (signal.aborted) {
        break
      }
      const result = await fetchWithRetry(
        row.FeedingReportId,
        signal,
        fetchDetail
      )
      results.set(row.FeedingReportId, result)
      onProgress(++done)
    }
  }
  await Promise.all(Array.from({ length: CONCURRENCY }, worker))
  return results
}
